import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  ArrowRight,
  Coins,
  Download,
  ExternalLink,
  Phone,
  Shield,
  Zap,
} from 'lucide-react';
import {
  ESSENCE_SITE_URL,
  SELFOS_BETA,
  SELFOS_DOWNLOAD_SELF_LABS_ESSENCE,
} from '../../../lib/selfOsDownload';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

const PILLARS = [
  {
    icon: Phone,
    title: 'Node rewards',
    text: 'Self OS phones that contribute verified idle compute earn Essence on a cloud ledger first — phones do not mint on-chain.',
  },
  {
    icon: Coins,
    title: 'Agent settlement',
    text: 'Agents can transfer and settle with Essence on Base for goods, services, and agent-to-agent payments.',
  },
  {
    icon: Shield,
    title: 'Treasury ↔ USDC',
    text: 'When funded, sellers can hold Essence or exit toward USDC via the treasury rail. Peg is notional — not FDIC or a regulated stablecoin.',
  },
];

export default function EssencePage() {
  return (
    <>
      <Helmet>
        <title>Essence — Stablecoin on Base | SELF Labs</title>
        <meta
          name="description"
          content="Essence is SELF Labs / Role Model’s Base ERC-20 stablecoin — Self OS node rewards, agent payments, and a treasury rail toward USDC."
        />
      </Helmet>

      <div
        id="essence"
        data-avatar-target="essence"
        className="min-h-screen bg-black text-white"
      >
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        <section className="relative px-4 pb-16 pt-28 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-3xl">
            <Link
              to="/products"
              className="group mb-8 inline-flex items-center text-sm text-white/50 transition-colors hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
              All products
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Zap className="h-3.5 w-3.5" />
                Settlement on Base · SELF OS economy
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">Essence</h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                The stablecoin for SELF OS — earn on Self OS phone, convert Talks via Concierge, and settle agent
                payments on Base.
              </p>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={ESSENCE_SITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] transition-transform hover:scale-[1.02] hover:bg-white/90"
                >
                  Open Essence site
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={SELFOS_DOWNLOAD_SELF_LABS_ESSENCE}
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] hover:border-white/35 hover:bg-white/10"
                >
                  <Download className="h-4 w-4" />
                  Download SELF OS beta
                </a>
              </div>
              <p className="mx-auto mt-4 max-w-md text-sm text-white/45">
                Android 9+ · sideload required · public beta v{SELFOS_BETA.version}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">How it works</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                Inference spend funds the loop
              </h2>
              <p className="mt-4 text-base leading-relaxed text-white/55">
                AI companies buy Resonatia inference; that demand backs Essence for nodes and agent settlement.
                Explore the full story, contracts, and economic layer on the dedicated Essence site.
              </p>
            </motion.div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {PILLARS.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                >
                  <item.icon className="h-8 w-8 text-white/80" />
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <motion.div
              {...fadeUp}
              className="rounded-2xl border border-white/12 bg-white/[0.03] p-8 text-center sm:p-10"
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">SELF OS phone</p>
              <h2 className="mt-3 text-2xl font-bold sm:text-3xl">Get the Android beta</h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/55">
                Earn Essence and Talk with Concierge on your phone. Public beta — prototype; Essence is not cash;
                compute may use battery if you opt in.
              </p>
              <a
                href={SELFOS_DOWNLOAD_SELF_LABS_ESSENCE}
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
              >
                <Download className="h-4 w-4" />
                Download SELF OS {SELFOS_BETA.version}
              </a>
              <p className="mt-4 text-sm text-white/45">
                Support:{' '}
                <a href={`mailto:${SELFOS_BETA.supportEmail}`} className="text-white/70 hover:text-white">
                  {SELFOS_BETA.supportEmail}
                </a>
                {' · '}
                <a
                  href="https://rolemodel.ai/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white"
                >
                  Privacy
                </a>
                {' · '}
                <a
                  href="https://rolemodel.ai/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white"
                >
                  Terms
                </a>
              </p>
            </motion.div>
          </div>
        </section>

        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <a
              href={ESSENCE_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
            >
              Full Essence site
              <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/essence-disclaimer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10"
            >
              Disclaimer
            </Link>
            <Link
              to="/resonatia"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10"
            >
              Resonatia
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
